// import node module libraries
import { Fragment, useEffect, useState } from 'react';
import { Alert, Row, Col, Breadcrumb } from 'react-bootstrap';
import { ExclamationCircleFill, CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';

// Import Loading Animation
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

// import sub components
import FlowSummary from './FlowSummary';
import ConfigurationSection from './ConfigrationSection';
import RecentFlowsOutputs from './RecentFlowsOutputs';
import Status from './Status';
import OverallProgressChart from './OverallProgressChart';
import RecentActivity from './RecentActivity';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// Import translation
import { useTranslation } from 'react-i18next';

const FlowConfigurationSingle = () => {
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });

	const urlParams = useParams();

	const auth = useAuthUser(); // Assuming this gets the auth user data

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// Marketplace Flows
	const [MarketplaceFlow, setMarketplaceFlow] = useState({});
	const [isLoadingMarketplaceFlow, setIsLoadingMarketplaceFlow] = useState(true);
	const [isFailedMarketplaceFlow, setIsFailedMarketplaceFlow] = useState(null);

	const getMarketplaceFlow = async () => {
		const flowId = FlowConfig.config_key.split('.')[1]; // Extract the Flow id from the config_key

		setIsLoadingMarketplaceFlow(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/public/marketplaceentries/${flowId}`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setMarketplaceFlow(responseData); // Map the data to match the expected keys
				setIsLoadingMarketplaceFlow(false);
				setIsFailedMarketplaceFlow(null);
			})
			.catch((error) => {
				setIsLoadingMarketplaceFlow(false);
				setIsFailedMarketplaceFlow(error);
			});
	}

	// Flow Config
	const [isLoadingFlowConfig, setIsLoadingFlowConfig] = useState(true);
	const [isFailedFlowConfig, setIsFailedFlowConfig] = useState(null);
	const [FlowConfig, setFlowConfig] = useState({});
	const [isLoadedFlowConfig, setIsLoadedFlowConfig] = useState(null); // used to check if the marketplace flows are loaded


	// API-Call for all Data
	const getFlowConfig = async () => {
		setIsLoadingFlowConfig(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/configs/${urlParams.id}`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setFlowConfig(responseData);
				setIsLoadingFlowConfig(false);
				setIsFailedFlowConfig(null);
				setIsLoadedFlowConfig(true);
			})
			.catch((error) => {
				setIsLoadingFlowConfig(false);
				setIsFailedFlowConfig(error);
			});
	};

	const [isLoadingStatusButton, setIsLoadingStatusButton] = useState(false);

	// Update Flow Config Status
	const handleChangeStatus = async (status) => {
		setIsLoadingStatusButton(true);

		const data = {
			...FlowConfig,
			config_value: {
				...FlowConfig.config_value,
				status: status
			}
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/tenants/${auth.tenant_id}/configs/${urlParams.id}`, data, options)
			.then(response => {
				// Update the FlowConfig in the data state
				const responseData = response.data.result;
				setTimeout(() => {
					setIsLoadingStatusButton(false);
					setFlowConfig(responseData);
				}, 500); // Reset after 1 second
				
				// Show success notification
				//handleShowNotification(setNotification, 'success', t('Success'), t('Flow status updated successfully'));
			})
			.catch((error) => {
				setIsLoadingStatusButton(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				// Show error notification
				handleShowNotification(setNotification, 'danger', t('Error'), errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	}

	// Get Flow Logs
	const [isLoadingFlowLogs, setIsLoadingFlowLogs] = useState(true);
	const [isFailedFlowLogs, setIsFailedFlowLogs] = useState(null);
	const [FlowLogs, setFlowLogs] = useState({});
	const handleReloadFlowLogs = () => {
		setIsLoadingFlowLogs(true);
		setTimeout(() => {
			getFlowLogs();
		}, 1000);
	};

	// API-Call for Flows
	const getFlowLogs = async () => {
		setIsLoadingFlowLogs(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/logs?log_key=${FlowConfig.config_key}`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setFlowLogs(responseData);
				setIsLoadingFlowLogs(false);
				setIsFailedFlowLogs(null);
			})
			.catch((error) => {
				setIsLoadingFlowLogs(false);
				setIsFailedFlowLogs(error);
				handleShowNotification(setNotification, 'danger', t('Error'), t(error.message));
			});
	};

	// Get Flow Outputs
	const [isLoadingFlowOutputs, setIsLoadingFlowOutputs] = useState(true);
	const [isFailedFlowOutputs, setIsFailedFlowOutputs] = useState(null);
	const [FlowOutputs, setFlowOutputs] = useState({});
	const handleReloadFlowOutputs = () => {
		setIsLoadingFlowOutputs(true);
		setTimeout(() => {
			getFlowOutputs();
		}, 1000);
	};

	// API-Call for Flows
	const getFlowOutputs = async () => {
		setIsLoadingFlowOutputs(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/flows?config_id=${urlParams.id}`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setFlowOutputs(responseData);
				setIsLoadingFlowOutputs(false);
				setIsFailedFlowOutputs(null);
			})
			.catch((error) => {
				setIsLoadingFlowOutputs(false);
				setIsFailedFlowOutputs(error);
				handleShowNotification(setNotification, 'danger', t('Error'), t(error.message));
			});
	};

	useEffect(() => {
		getFlowConfig();
	}, []);

	useEffect(() => {
		getFlowOutputs();
	}, []);

	useEffect(() => {
		if (FlowConfig.config_key !== undefined && FlowConfig.config_key !== null) {
			getFlowLogs();
		}
	}, [isLoadedFlowConfig]);

	useEffect(() => {
		if (FlowConfig.config_key !== undefined && FlowConfig.config_key !== null) {
			getMarketplaceFlow();
		}
	}, [isLoadedFlowConfig]);

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			{/* header and breadcrumb */}
			<Row>
				<Col lg={12} md={12} sm={12}>
					<div className="border-bottom pb-4 mb-4 d-md-flex align-items-center justify-content-between">
						<div className="mb-3 mb-md-0">
							<h1 className="mb-1 h2 font-weight-bold">
								{MarketplaceFlow && !isLoadingMarketplaceFlow && !isFailedMarketplaceFlow ? MarketplaceFlow.marketplace_value.title : <Skeleton className="rounded" count={1} height={30} />}
							</h1>
							<Breadcrumb>
								<Breadcrumb.Item href="#">{t('Dashboard')}</Breadcrumb.Item>
								<Breadcrumb.Item href="#">{t('Marketplace')}</Breadcrumb.Item>
								<Breadcrumb.Item href="/dashboard/marketplace/flows">{t('Flows')}</Breadcrumb.Item>
								<Breadcrumb.Item active>{urlParams.id}</Breadcrumb.Item>
							</Breadcrumb>
						</div>
						<div className="d-flex align-items-center">
						</div>
					</div>
				</Col>
			</Row>
			{isLoadingFlowConfig ? (
			<Row>
				<Col md={12} xl={8} xs={12}>
					<Row>
						<Col xs={12} className="mb-4">
							{/* Flow summary section */}
							<Skeleton className="rounded" count={1} height={350} />
						</Col>
						<Col md={12} className="mb-4">
							{/* Flow configuration section */}
							<Skeleton className="rounded" count={1} height={450} />
						</Col>
						<Col md={12} className="mb-4">
							{/* Flow recent flows table section */}
							<Skeleton className="rounded" count={1} height={350} />
						</Col>
					</Row>
				</Col>
				<Col md={12} xl={4} xs={12}>
					{/* Flow status section */}
					<Skeleton className="rounded mb-4" count={1} height={200} />

					{/* Flow overall configration progress chart  */}
					<Skeleton className="rounded mb-4" count={1} height={400} />

					{/* Flow recent activities section  */}
					<Skeleton className="rounded mb-4" count={1} height={600} />
				</Col>
			</Row>
			) : isFailedFlowConfig ? (
				<Col className="text-center">
					<Alert variant="danger" className="bg-danger rounded">
						<ExclamationTriangleFill className="me-2" />
						{t('Error loading Flow')}
					</Alert>
				</Col>
			) : null}
			
			<Row>
				<Col md={12} xl={8} xs={12}>
					<Row>
						{MarketplaceFlow && !isLoadingMarketplaceFlow && !isFailedMarketplaceFlow ? (
							<>
								<Col xs={12} className="mb-4">
									{/* Flow summary section */}
									<FlowSummary item={MarketplaceFlow} />
								</Col>
							</>
						) : null}

						{FlowConfig && MarketplaceFlow && !isLoadingFlowConfig && !isFailedFlowConfig && !isLoadingMarketplaceFlow && !isFailedMarketplaceFlow ? (
							<>
								<Col md={12} className="mb-4">
									{/* Flow configuration section */}
									<ConfigurationSection selectedFlow={MarketplaceFlow} item={FlowConfig} onUpdate={setFlowConfig}/>
								</Col>
							</>
						) : null}

						{/* Flow recent flows table section */}
						{isLoadingFlowOutputs ? (
							<Col className="text-center">
								<Skeleton className="rounded" count={1} height={200} />
							</Col>
						) : isFailedFlowOutputs ? (
							<Col className="mb-4 text-center">
								<Alert variant="warning" className="bg-warning rounded text-dark">
									<ExclamationTriangleFill className="me-2" />
									{t('Error loading Flows')}
								</Alert>
							</Col>
						) : FlowOutputs && !isLoadingFlowOutputs && !isFailedFlowOutputs ? (
							<>
								<Col md={12} className="mb-4">
									<RecentFlowsOutputs item={FlowOutputs} onReload={handleReloadFlowOutputs} />
								</Col>
							</>
						) : null}
						
					</Row>
				</Col>
				<Col md={12} xl={4} xs={12}>
					{/* Flow status section */}
					{FlowConfig && !isLoadingFlowConfig && !isFailedFlowConfig ? (
						<>
							<Col md={12} className="mb-4">
								<Status item={FlowConfig} onSubmit={handleChangeStatus} isLoadingButton={isLoadingStatusButton}/>
							</Col>
						</>
					) : null}

					{/* Flow overall configration progress chart  */}
					{FlowConfig && !isLoadingFlowConfig && !isFailedFlowConfig ? (
						<>
							<Col md={12} className="mb-4">
								<OverallProgressChart item={FlowConfig} />
							</Col>
						</>
					) : null}

					{/* Flow recent activities section  */}
					{isLoadingFlowLogs ? (
						<Col className="text-center">
							<Skeleton className="rounded" count={1} height={600} />
						</Col>
					) : isFailedFlowLogs ? (
						<Col className="mb-4 text-center">
							<Alert variant="warning" className="bg-warning rounded text-dark">
								<ExclamationTriangleFill className="me-2" />
								{t('Error loading Flow Logs')}
							</Alert>
						</Col>
					) : FlowLogs && !isLoadingFlowLogs && !isFailedFlowLogs ? (
						<>
							<Col md={12} className="mb-4">
								<RecentActivity item={FlowLogs} onReload={handleReloadFlowLogs} />
							</Col>
						</>
					) : null}
				</Col>
			</Row>
		</Fragment>
	);
};

export default FlowConfigurationSingle;
